import { useRef, useEffect, memo } from 'react';
import * as d3 from 'd3';
import type { BarChartDataPoint } from '@/types/analytics';
import styles from './BarChart.module.scss';

export interface BarChartProps {
  data: BarChartDataPoint[];
  width: number;
  height: number;
  horizontal?: boolean;
  showValues?: boolean;
  animate?: boolean;
  colorScheme?: 'primary' | 'success' | 'warning' | 'error' | 'custom';
}

/**
 * Reusable D3 Bar Chart Component
 *
 * Supports both vertical and horizontal orientations with smooth animations.
 * Uses CSS custom properties for theming and automatic color adaptation.
 * Optimized with React.memo and useMemo for performance.
 *
 * @example
 * <BarChart
 *   data={[
 *     { label: 'Easy', value: 85, color: 'var(--success)' },
 *     { label: 'Medium', value: 70, color: 'var(--warning)' },
 *     { label: 'Hard', value: 45, color: 'var(--error)' }
 *   ]}
 *   width={400}
 *   height={300}
 *   showValues
 * />
 */
export const BarChart = memo(function BarChart({
  data,
  width,
  height,
  horizontal = false,
  showValues = false,
  animate = true,
  colorScheme = 'primary',
}: BarChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  // Memoize inner dimensions
  // const innerWidth = useMemo(() => width - margins.left - margins.right, [width, margins]);
  // const innerHeight = useMemo(() => height - margins.top - margins.bottom, [height, margins]);

  useEffect(() => {
    if (!svgRef.current || !data || data.length === 0 || width === 0 || height === 0) {
      return;
    }

    // Clear previous chart
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Chart margins - increase left margin for horizontal charts to accommodate longer labels
    const margin = {
      top: 20,
      right: horizontal ? 50 : 20, // Extra space for value labels in horizontal mode
      bottom: horizontal ? 40 : 40,
      left: horizontal ? 90 : 60,  // More space for question type labels in horizontal mode
    };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Create chart group
    const chart = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    if (!horizontal) {
      // Vertical Bar Chart
      // X Scale (categorical)
      const xScale = d3
        .scaleBand()
        .domain(data.map((d) => d.label))
        .range([0, chartWidth])
        .padding(0.2);

      // Y Scale (linear)
      const yScale = d3
        .scaleLinear()
        .domain([0, d3.max(data, (d) => d.value) || 100])
        .range([chartHeight, 0])
        .nice();

      // X Axis
      chart
        .append('g')
        .attr('class', styles.barChart__axis)
        .attr('transform', `translate(0,${chartHeight})`)
        .call(d3.axisBottom(xScale))
        .selectAll('text')
        .style('text-anchor', 'middle');

      // Y Axis
      chart
        .append('g')
        .attr('class', styles.barChart__axis)
        .call(d3.axisLeft(yScale).ticks(5));

      // Bars
      const bars = chart
        .selectAll('.bar')
        .data(data)
        .join('rect')
        .attr('class', styles.barChart__bar)
        .attr('x', (d) => xScale(d.label) || 0)
        .attr('width', xScale.bandwidth())
        .attr('fill', (d) => d.color || getColorForScheme(colorScheme))
        .attr('rx', 4);

      if (animate) {
        bars
          .attr('y', chartHeight)
          .attr('height', 0)
          .transition()
          .duration(800)
          .ease(d3.easeCubicOut)
          .attr('y', (d) => yScale(d.value))
          .attr('height', (d) => chartHeight - yScale(d.value));
      } else {
        bars
          .attr('y', (d) => yScale(d.value))
          .attr('height', (d) => chartHeight - yScale(d.value));
      }

      // Value labels
      if (showValues) {
        chart
          .selectAll('.value-label')
          .data(data)
          .join('text')
          .attr('class', styles.barChart__valueLabel)
          .attr('x', (d) => (xScale(d.label) || 0) + xScale.bandwidth() / 2)
          .attr('y', (d) => yScale(d.value) - 5)
          .attr('text-anchor', 'middle')
          .text((d) => (typeof d.value === 'number' ? parseFloat(d.value.toFixed(2)) : d.value));
      }
    } else {
      // Horizontal Bar Chart
      // X Scale (linear)
      const xScale = d3
        .scaleLinear()
        .domain([0, d3.max(data, (d) => d.value) || 100])
        .range([0, chartWidth])
        .nice();

      // Y Scale (categorical)
      const yScale = d3
        .scaleBand()
        .domain(data.map((d) => d.label))
        .range([0, chartHeight])
        .padding(0.2);

      // X Axis
      chart
        .append('g')
        .attr('class', styles.barChart__axis)
        .attr('transform', `translate(0,${chartHeight})`)
        .call(d3.axisBottom(xScale).ticks(5));

      // Y Axis
      chart
        .append('g')
        .attr('class', styles.barChart__axis)
        .call(d3.axisLeft(yScale));

      // Bars
      const bars = chart
        .selectAll('.bar')
        .data(data)
        .join('rect')
        .attr('class', styles.barChart__bar)
        .attr('y', (d) => yScale(d.label) || 0)
        .attr('height', yScale.bandwidth())
        .attr('fill', (d) => d.color || getColorForScheme(colorScheme))
        .attr('rx', 4);

      if (animate) {
        bars
          .attr('x', 0)
          .attr('width', 0)
          .transition()
          .duration(800)
          .ease(d3.easeCubicOut)
          .attr('width', (d) => xScale(d.value));
      } else {
        bars.attr('x', 0).attr('width', (d) => xScale(d.value));
      }

      // Value labels
      if (showValues) {
        const maxValue = d3.max(data, (d) => d.value) || 100;

        chart
          .selectAll('.value-label')
          .data(data)
          .join('text')
          .attr('class', styles.barChart__valueLabel)
          .attr('x', (d) => {
            const barWidth = xScale(d.value);
            const barPercentage = d.value / maxValue;
            // If bar is more than 80% full, place label inside the bar (left-aligned)
            // Otherwise place it outside the bar (right of bar end)
            return barPercentage > 0.8 ? barWidth - 10 : barWidth + 5;
          })
          .attr('y', (d) => (yScale(d.label) || 0) + yScale.bandwidth() / 2)
          .attr('dominant-baseline', 'middle')
          .attr('text-anchor', (d) => {
            const barPercentage = d.value / maxValue;
            // Right-align text when inside bar, left-align when outside
            return barPercentage > 0.8 ? 'end' : 'start';
          })
          .attr('fill', (d) => {
            const barPercentage = d.value / maxValue;
            // White text when inside bar for contrast
            return barPercentage > 0.8 ? 'white' : 'currentColor';
          })
          .text((d) => (typeof d.value === 'number' ? parseFloat(d.value.toFixed(2)) : d.value));
      }
    }
  }, [data, width, height, horizontal, showValues, animate, colorScheme]);

  if (width === 0 || height === 0) {
    return null;
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className={styles.barChart}
      role="img"
      aria-label="Bar chart visualization"
    />
  );
});

function getColorForScheme(scheme: string): string {
  switch (scheme) {
    case 'success':
      return 'var(--success)';
    case 'warning':
      return 'var(--warning)';
    case 'error':
      return 'var(--error)';
    case 'primary':
    default:
      return 'var(--primary)';
  }
}
